import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { formatDate } from '../../utils/dateUtils';

interface ChatMessage {
  id: string;
  content: string;
  createdAt: string;
  leagueId: string;
  league: {
    id: string;
    name: string;
    season: { name: string };
  };
  user: {
    id: string;
    teamName: string;
  };
  mentionedUser?: { id: string; teamName: string } | null;
}

const PAGE_SIZE = 50;

const AdminChatModeration: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [editContent, setEditContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchMessages = useCallback(async (before?: string) => {
    try {
      if (before) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params: Record<string, string | number> = { limit: PAGE_SIZE };
      if (before) {
        params.before = before;
      }

      const response = await apiClient.get('/admin/chat-messages', { params });
      const fetched: ChatMessage[] = response.data.messages;

      setMessages(prev => (before ? [...prev, ...fetched] : fetched));
      setHasMore(response.data.hasMore);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load chat messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleLoadMore = () => {
    if (!hasMore || loadingMore || messages.length === 0) return;
    const oldest = messages[messages.length - 1];
    fetchMessages(oldest.createdAt);
  };

  const openEditModal = (message: ChatMessage) => {
    setEditingMessage(message);
    setEditContent(message.content);
  };

  const closeEditModal = () => {
    setEditingMessage(null);
    setEditContent('');
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMessage || !editContent.trim()) return;

    setSubmitting(true);
    try {
      const response = await apiClient.put(`/admin/chat-messages/${editingMessage.id}`, {
        content: editContent.trim()
      });
      setMessages(prev =>
        prev.map(message => (message.id === editingMessage.id ? response.data : message))
      );
      closeEditModal();
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (message: ChatMessage) => {
    if (deletingId) return;
    if (!window.confirm(`Delete this message from ${message.user.teamName} in ${message.league.name}?`)) {
      return;
    }

    setDeletingId(message.id);
    try {
      await apiClient.delete(`/admin/chat-messages/${message.id}`);
      setMessages(prev => prev.filter(m => m.id !== message.id));
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete message');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredMessages = messages.filter(message => {
    if (!searchTerm.trim()) return true;
    const query = searchTerm.toLowerCase();
    return (
      message.content.toLowerCase().includes(query) ||
      message.user.teamName.toLowerCase().includes(query) ||
      message.league.name.toLowerCase().includes(query) ||
      message.league.season.name.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-xl" style={{ color: 'var(--text-primary)' }}>Loading chat messages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/admin" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Admin Dashboard
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Chat Moderation
          </h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Review league chat messages across all leagues
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="mb-6">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by message, user, league, or competition..."
            className="w-full px-4 py-2 rounded-lg border"
            style={{
              backgroundColor: 'var(--surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        <div className="card rounded-lg shadow overflow-hidden">
          {filteredMessages.length === 0 ? (
            <div className="p-12 text-center" style={{ color: 'var(--text-secondary)' }}>
              {messages.length === 0 ? 'No chat messages yet.' : 'No messages match your search.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
                <thead style={{ backgroundColor: 'rgba(156, 163, 175, 0.1)' }}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>League</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[280px]" style={{ color: 'var(--text-secondary)' }}>Message</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {filteredMessages.map(message => (
                    <tr key={message.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                      <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {formatDate(message.createdAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Link
                          to={`/users/${message.user.id}`}
                          className="text-sm font-medium hover:underline"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {message.user.teamName}
                        </Link>
                      </td>
                      <td className="px-4 py-4">
                        <Link
                          to={`/leagues/${message.league.id}`}
                          className="text-sm font-medium hover:underline block"
                          style={{ color: 'var(--highlight)' }}
                        >
                          {message.league.name}
                        </Link>
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          {message.league.season.name}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                        {message.content}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => openEditModal(message)}
                          className="mr-3 font-medium hover:underline"
                          style={{ color: 'var(--highlight)' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(message)}
                          disabled={deletingId === message.id}
                          className="font-medium hover:underline disabled:opacity-50"
                          style={{ color: '#ef4444' }}
                        >
                          {deletingId === message.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {hasMore && !searchTerm && (
          <div className="mt-6 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="btn-primary px-6 py-2 rounded-md disabled:opacity-50"
            >
              {loadingMore ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}
      </div>

      {editingMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="card rounded-lg shadow w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Edit Message
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {editingMessage.user.teamName} in {editingMessage.league.name}
            </p>
            <form onSubmit={handleSaveEdit}>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                maxLength={500}
                rows={4}
                className="w-full px-3 py-2 rounded-lg border mb-2"
                style={{
                  backgroundColor: 'var(--surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-primary)'
                }}
                required
              />
              <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
                {editContent.length}/500 characters
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 rounded-md border"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !editContent.trim()}
                  className="btn-primary px-4 py-2 rounded-md disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChatModeration;
